<div class="content-info bulk-add">
  <div
    class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2"
  >
    <div class="d-flex align-items-center gap-3">
      <h3 class="mb-0">Bulk Add Products</h3>
      @if (lookups) {
        <span
          class="badge rounded-pill"
          [class.text-bg-secondary]="!skuIsAuto"
          [class.text-bg-info]="skuIsAuto"
          title="Set under Configuration → Products → SKU entry on Bulk Add Products"
        >
          SKU: {{ skuIsAuto ? "auto-generated on save" : "manual entry" }}
        </span>
      }
    </div>

    <div class="d-flex flex-wrap align-items-center gap-2 toolbar">
      <a
        class="btn btn-link text-decoration-none px-1"
        [routerLink]="['/product-list']"
      >
        <i class="bi bi-arrow-left"></i> Product List
      </a>
      <button
        type="button"
        class="btn btn-outline-secondary"
        (click)="openColumnsPicker()"
      >
        <i class="bi bi-layout-three-columns"></i> Columns
      </button>
      <button
        type="button"
        class="btn btn-outline-secondary"
        (click)="addRows(10)"
      >
        <i class="bi bi-plus-lg"></i> 10 rows
      </button>
      <button
        type="button"
        class="btn btn-outline-danger"
        (click)="clearGrid()"
      >
        <i class="bi bi-eraser"></i> Clear
      </button>
      <button
        type="button"
        class="btn btn-success text-nowrap"
        (click)="save()"
        [disabled]="saving || validating || loadingLookups || lookupsError"
      >
        @if (saving) {
          <span class="spinner-border spinner-border-sm me-1"></span> Saving…
        } @else if (validating) {
          <span class="spinner-border spinner-border-sm me-1"></span> Checking…
        } @else {
          <i class="bi bi-save"></i> Save
          @if (filledRowCount) {
            ({{ filledRowCount }})
          }
        }
      </button>
    </div>
  </div>

  <p class="text-muted small mb-2">
    One row per SKU. <span class="text-danger">*</span> columns are mandatory;
    open <strong>Columns</strong> to show more fields. Paste a block from Excel
    into any text cell to fill several rows at once, and use
    <i class="bi bi-arrow-bar-down"></i> on a header to copy that cell down.
    Empty rows are ignored. All rows are saved together — nothing is created
    until every row is valid.
  </p>

  @if (lastResult) {
    <div class="alert alert-success d-flex align-items-start gap-2 py-2">
      <i class="bi bi-check-circle-fill mt-1"></i>
      <div class="flex-grow-1">
        <strong>Created {{ lastResult.created }} product(s).</strong>
        <span class="small text-break">{{ lastResult.skus.join(", ") }}</span>
      </div>
      <button
        type="button"
        class="btn btn-sm btn-outline-success text-nowrap"
        (click)="goToList()"
      >
        View in Product List
      </button>
      <button
        type="button"
        class="btn-close"
        aria-label="Dismiss"
        (click)="lastResult = null"
      ></button>
    </div>
  }

  @if (lookupsError) {
    <div class="alert alert-danger d-flex align-items-center gap-2 py-2">
      <i class="bi bi-exclamation-triangle-fill"></i>
      <span>Dropdown data could not be loaded.</span>
      <button
        type="button"
        class="btn btn-sm btn-outline-danger ms-auto"
        (click)="loadLookups()"
      >
        Retry
      </button>
    </div>
  }

  <div class="grid-wrap" [class.is-loading]="loadingLookups">
    <table
      class="table table-bordered table-sm grid-table mb-0"
      [style.width.px]="tableWidth"
    >
      <!-- Column widths live here: with table-layout: fixed the browser
           honours <col> widths exactly, which the frozen offsets rely on. -->
      <colgroup>
        <col class="col-row-num" />
        @for (col of visibleColumns; track col.key) {
          <col [style.width.px]="col.width" />
        }
        <col class="col-row-actions" />
      </colgroup>
      <thead>
        <tr>
          <th class="row-num sticky-left">#</th>
          @for (col of visibleColumns; track col.key) {
            <th
              [class.sticky-left]="frozenLeft[col.key] != null"
              [class.sticky-left-edge]="col.key === lastFrozenKey"
              [style.position]="frozenLeft[col.key] != null ? 'sticky' : null"
              [style.left.px]="frozenLeft[col.key] ?? null"
              [title]="col.hint || ''"
            >
              <div
                class="d-flex align-items-center justify-content-between gap-1"
              >
                <span class="text-truncate">
                  {{ col.label }}
                  @if (isRequired(col)) {
                    <span class="text-danger">*</span>
                  }
                </span>
                @if (!(col.key === "sku" && skuIsAuto)) {
                  <button
                    type="button"
                    class="btn btn-link btn-fill p-0"
                    tabindex="-1"
                    [title]="
                      'Copy ' + col.label + ' down from the selected row'
                    "
                    (click)="fillDown(col)"
                  >
                    <i class="bi bi-arrow-bar-down"></i>
                  </button>
                }
              </div>
            </th>
          }
          <th class="row-actions sticky-right"></th>
        </tr>
      </thead>
      <tbody>
        @for (row of rows; track row.uid; let ri = $index) {
          <tr
            [class.row-has-error]="(row.errors | keyvalue).length"
            [class.row-empty]="isRowEmpty(row)"
          >
            <td class="row-num sticky-left">{{ ri + 1 }}</td>
            @for (col of visibleColumns; track col.key) {
              <td
                class="cell"
                [class.has-error]="row.errors[col.key]"
                [class.has-warning]="
                  !row.errors[col.key] && row.warnings[col.key]
                "
                [class.sticky-left]="frozenLeft[col.key] != null"
                [class.sticky-left-edge]="col.key === lastFrozenKey"
                [style.position]="frozenLeft[col.key] != null ? 'sticky' : null"
                [style.left.px]="frozenLeft[col.key] ?? null"
                [attr.data-cell]="ri + ':' + col.key"
                [title]="row.errors[col.key] || row.warnings[col.key] || ''"
              >
                @switch (col.type) {
                  @case ("text") {
                    @if (col.key === "sku" && skuIsAuto) {
                      <input
                        type="text"
                        class="cell-input"
                        value=""
                        placeholder="Auto"
                        disabled
                      />
                    } @else {
                      <input
                        type="text"
                        class="cell-input"
                        [placeholder]="col.placeholder || ''"
                        [maxlength]="col.maxLength || null"
                        [(ngModel)]="row.values[col.key]"
                        (ngModelChange)="onCellChange(row, col.key)"
                        (focus)="setActive(ri, col.key)"
                        (paste)="onPaste($event, ri, col.key)"
                        (keydown.enter)="moveDown($event, ri, col.key)"
                        autocomplete="off"
                      />
                    }
                  }
                  @case ("number") {
                    <input
                      type="number"
                      class="cell-input text-end"
                      step="any"
                      min="0"
                      [(ngModel)]="row.values[col.key]"
                      (ngModelChange)="onCellChange(row, col.key)"
                      (focus)="setActive(ri, col.key)"
                      (paste)="onPaste($event, ri, col.key)"
                      (keydown.enter)="moveDown($event, ri, col.key)"
                    />
                  }
                  @case ("bool") {
                    <select
                      class="cell-input"
                      [(ngModel)]="row.values[col.key]"
                      (ngModelChange)="onCellChange(row, col.key)"
                      (focus)="setActive(ri, col.key)"
                      (keydown.enter)="moveDown($event, ri, col.key)"
                    >
                      @for (o of boolOptions; track o.name) {
                        <option [ngValue]="o.id">{{ o.name }}</option>
                      }
                    </select>
                  }
                  @case ("select") {
                    @if (col.options === "manufacturers") {
                      <ng-select
                        class="cell-select"
                        [items]="optionsFor(row, col)"
                        bindLabel="name"
                        bindValue="id"
                        groupBy="group"
                        [clearable]="true"
                        [searchable]="true"
                        appendTo="body"
                        placeholder="Select"
                        [(ngModel)]="row.values[col.key]"
                        (change)="onCellChange(row, col.key)"
                        (focus)="setActive(ri, col.key)"
                      >
                      </ng-select>
                    } @else {
                      <ng-select
                        class="cell-select"
                        [items]="optionsFor(row, col)"
                        bindLabel="name"
                        bindValue="id"
                        [clearable]="true"
                        [searchable]="true"
                        appendTo="body"
                        placeholder="Select"
                        [(ngModel)]="row.values[col.key]"
                        (change)="onCellChange(row, col.key)"
                        (focus)="setActive(ri, col.key)"
                      >
                      </ng-select>
                    }
                  }
                }
              </td>
            }
            <td class="row-actions sticky-right text-nowrap">
              <button
                type="button"
                class="btn btn-link p-0 me-1 text-secondary"
                tabindex="-1"
                title="Duplicate row below"
                (click)="duplicateRow(ri)"
              >
                <i class="bi bi-files"></i>
              </button>
              <button
                type="button"
                class="btn btn-link p-0 text-danger"
                tabindex="-1"
                title="Remove row"
                (click)="removeRow(ri)"
              >
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
        }
      </tbody>
    </table>
    @if (loadingLookups) {
      <div class="grid-loading">
        <span class="spinner-border spinner-border-sm me-2"></span> Loading
        dropdowns…
      </div>
    }
  </div>

  <div class="d-flex align-items-center gap-2 mt-2">
    <button
      type="button"
      class="btn btn-sm btn-outline-secondary"
      (click)="addRows(10)"
    >
      <i class="bi bi-plus-lg"></i> Add 10 rows
    </button>
    <span class="text-muted small"
      >{{ rows.length }} rows on the grid · {{ filledRowCount }} filled</span
    >
  </div>

  @if (errorList.length) {
    <div class="card mt-3 border-danger error-panel">
      <div class="d-flex align-items-center gap-2 mb-2">
        <i class="bi bi-exclamation-octagon-fill text-danger"></i>
        <strong>{{ errorList.length }} problem(s) to fix</strong>
        <span class="text-muted small"
          >— click one to jump to the cell. Nothing is saved until the list is
          empty.</span
        >
      </div>
      <ul class="list-unstyled mb-0 error-list">
        @for (e of errorList; track $index) {
          <li>
            <a href="javascript:void(0)" (click)="focusCell(e.row, e.column)">
              <span class="badge text-bg-light border">Row {{ e.row }}</span>
              <span class="fw-semibold">{{ columnLabel(e.column) }}:</span>
              {{ e.error }}
            </a>
          </li>
        }
      </ul>
    </div>
  }

  @if (warningList.length) {
    <div class="card mt-3 border-warning error-panel">
      <div class="d-flex align-items-center gap-2 mb-2">
        <i class="bi bi-exclamation-triangle-fill text-warning"></i>
        <strong>{{ warningList.length }} warning(s)</strong>
        <span class="text-muted small">— these rows can still be saved.</span>
      </div>
      <ul class="list-unstyled mb-0 error-list">
        @for (w of warningList; track $index) {
          <li>
            <a href="javascript:void(0)" (click)="focusCell(w.row, w.column)">
              <span class="badge text-bg-light border">Row {{ w.row }}</span>
              <span class="fw-semibold">{{ columnLabel(w.column) }}:</span>
              {{ w.error }}
            </a>
          </li>
        }
      </ul>
    </div>
  }
</div>

<!-- Columns picker -->
<ng-template #columnsModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title fst-italic">Columns</h5>
    <button
      type="button"
      class="btn-close"
      aria-label="Close"
      (click)="modal.dismiss()"
    ></button>
  </div>
  <div class="modal-body columns-picker">
    <h6 class="text-primary">Other columns</h6>
    <div class="form-check mb-1">
      <input
        class="form-check-input"
        type="checkbox"
        id="col-select-all"
        [checked]="allOptionalSelected"
        (change)="toggleAllOptional($any($event.target).checked)"
      />
      <label class="form-check-label" for="col-select-all">Select all</label>
    </div>
    @for (col of optionalColumns; track col.key) {
      <div class="form-check">
        <input
          class="form-check-input"
          type="checkbox"
          [id]="'col-' + col.key"
          [checked]="pickerSelection.has(col.key)"
          (change)="togglePickerColumn(col.key, $any($event.target).checked)"
        />
        <label class="form-check-label" [for]="'col-' + col.key">
          {{ col.label }}
          @if (col.hint) {
            <small class="text-muted">— {{ col.hint }}</small>
          }
        </label>
      </div>
    }

    <h6 class="text-primary mt-3">Mandatory columns</h6>
    @for (col of mandatoryColumns; track col.key) {
      <div class="form-check">
        <input
          class="form-check-input"
          type="checkbox"
          checked
          disabled
          [id]="'mcol-' + col.key"
        />
        <label class="form-check-label text-muted" [for]="'mcol-' + col.key">{{
          col.label
        }}</label>
      </div>
    }
  </div>
  <div class="modal-footer justify-content-between">
    <button
      type="button"
      class="btn btn-outline-secondary"
      (click)="resetPicker()"
    >
      Reset
    </button>
    <button
      type="button"
      class="btn btn-primary"
      (click)="applyColumns(); modal.close()"
    >
      Apply
    </button>
  </div>
</ng-template>
